import {
	Grid,
	Box,
	TextField,
	Button,
	Typography,
	Divider,
	TableContainer,
	Paper,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import DataTable from "../../components/dataTable/DataTable";
import {
	DeleteClient,
	getClients,
} from "../../api";
import { useDispatch } from "react-redux";
import { hideLoader, showLoader } from "../../store/loaderSlice";
import DeleteIcon from "../../assets/images/DeteleIcon";
import EditIcon from "../../assets/images/EditIcon";
import { toast } from "react-toastify";
import type { Client } from "./ProjectDetail";
import {
	columnsProjectManagement as baseColumnsProjectManagement,
	filterDataByVisibleColumns,
} from "../../constants/common";
import AddIcon from "@mui/icons-material/Add";

const ProjectManagement = () => {
	const [projectList, setClientList] = useState<Client[]>([]);
	const [selected, setSelected] = useState<number[]>([]);
	const [search, setSearch] = useState("");
	const [searchTerm, setSearchTerm] = useState(""); // Immediate input
	const [page, setPage] = useState(0);
	const [rowsPerPage, setRowsPerPage] = useState(10);
	const [totalCount, setTotalCount] = useState(30);
	const navigate = useNavigate();

	const dispatch = useDispatch();

	useEffect(() => {
		fetchClientsList();
	}, [search, page, rowsPerPage]);

	// debouncing on search
	useEffect(() => {
		const delayDebounce = setTimeout(() => {
			setSearch(searchTerm); // Update debounced search after delay
		}, 1000); // Set debounce time as needed

		// Cleanup timeout if user types again before delay ends
		return () => clearTimeout(delayDebounce);
	}, [searchTerm]);

	const fetchClientsList = async () => {
		try {
			dispatch(showLoader());
			const response = await getClients(search, page + 1, rowsPerPage);
			if (response && response.success) {
				const mappedData = response.data.map((item: any) => ({
  ...item,
  project_id: item.client_id,
  index_name: item.client_name, // Map client_name to index_name for UI consistency
}));
setClientList(mappedData);
				setTotalCount(response.pagination.total_items);
			} else {
				const errorMsg = response?.payload || "Failed to fetch projects list";
				toast.error(errorMsg);
			}
		} catch (error) {
			let err: any = error;
			const errorMsg = err?.payload || "Failed to fetch projects list";
			toast.error(errorMsg);
		} finally {
			dispatch(hideLoader());
		}
	};

	const handleEditClient = (userData: Record<string, any>) => {
		navigate(`/homepage/project/${userData.project_id}`);
	};

	const handleUpdateClient = () => {
		if (selected.length === 1) {
			const selectedProject = projectList[selected[0]];
			if (selectedProject && selectedProject.project_id) {
				navigate(`/homepage/project/${selectedProject.project_id}`);
			} else {
				toast.error("Selected project does not have a valid ID");
			}
		} else {
			toast.error("Select one project to edit");
		}
	};

	const handleDeleteClient = async () => {
		try {
			dispatch(showLoader());
			type ClientType = { project_id: string };
			const selectedClientIds = selected
				.map((index) => (projectList[index] as ClientType)?.project_id)
				.filter(Boolean);
			const res = await DeleteClient({ project_ids: selectedClientIds });
			if (res.success) {
				toast.success(`Deleted ${selected.length} project(s)`);
				await fetchClientsList();
				setSelected([]);
			} else {
				const error = res?.payload["0"] || "Failed to delete Clients";
				toast.error(error);
			}
		} catch (error) {
			toast.error("An error occurred while deleting projects");
		} finally {
			dispatch(hideLoader());
		}
	};

	const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
		const target = e.target as HTMLInputElement;
		setSearchTerm(target.value);
	};

	return (
		<Grid
			container
			sx={{ width: "100%", justifyContent: "center", height: "95vh"}}
		>
			<Grid
				sx={{
					flex: 1,
					mt: 8,
					display: "flex",
					flexDirection: "column",
					alignItems: "center",
					background: "#fff",
					boxShadow:
						"0px 2px 4px rgba(0, 0, 0, 0.14), 0px 0px 2px rgba(0, 0, 0, 0.12)",
					borderRadius: "8px",
					width: "100%",
				}}
			>
				<Grid
					container
					sx={{ width: "100%" }}
					justifyContent="space-between"
				>
					<Typography
						align="center"
						sx={{ fontSize: "1.5rem", px: 2, p: 1.5 }}
					>
						Project Management
					</Typography>
					<Divider sx={{ width: "100%" }} />
				</Grid>

				<Grid
					container
					sx={{ width: "100%", p: 1.5 }}
					justifyContent="flex-end"
					alignItems="center"
				>
					<Grid item>
						<Button
							variant="contained"
							color="primary"
							startIcon={<AddIcon />}
							onClick={() => navigate("/homepage/project/new")}
						>
							Create Client
						</Button>
					</Grid>
						
				</Grid>

				<Grid container sx={{ width: "100%", p: 1.5 }}>
					<Grid item xs={12} sx={{ mb: 2 }}>
						<TextField
							label="Search"
							variant="outlined"
							fullWidth
							value={searchTerm}
							onChange={handleSearch}
							placeholder="Search projects..."
						/>
					</Grid>
				</Grid>

				{/* Table */}
				<Grid container sx={{ p: 1.5, pb: 5 }}>
					<Grid item xs={12}>
						<TableContainer component={Paper}>
							<DataTable
								data={projectList}
								columns={baseColumnsProjectManagement.map(column => {
									if (column.id === 'actions') {
										return {
											...column,
											render: (row: any) => (
												<Button
													startIcon={<EditIcon />}
													size="small"
													variant="outlined"
													color="primary"
													onClick={(e) => {
														e.stopPropagation();
														navigate(`/homepage/project/${row.project_id}`);
													}}
												>
													Edit
												</Button>
											)
										};
									}
									return column;
								})}
								selected={selected}
								setSelected={setSelected}
								page={page}
								setPage={setPage}
								totalCount={totalCount}
								rowsPerPage={rowsPerPage}
								setRowsPerPage={setRowsPerPage}
								onDelete={handleDeleteClient}
							/>
						</TableContainer>
					</Grid>
				</Grid>
			</Grid>
		</Grid>
	);
};

export default ProjectManagement;
